import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { Subject, takeUntil } from 'rxjs';
import { CriticalErrorNavigationService } from './critical-error-navigation.service';

type CriticalErrorIconType = 'connection' | 'server' | 'not-found' | 'unauthorized' | 'forbidden' | 'generic';

interface CriticalErrorContent {
  title: string;
  description: string;
  helper: string;
  iconType: CriticalErrorIconType;
}

@Component({
  selector: 'app-critical-error',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './critical-error.component.html',
  styleUrls: ['./critical-error.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CriticalErrorComponent implements OnInit, OnDestroy {
  errorCode = '500';
  returnUrl = '/';
  isReloading = false;
  backendMessage: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly criticalErrorNavigationService: CriticalErrorNavigationService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  get errorContent(): CriticalErrorContent {
    const code = Number(this.errorCode);

    if (code === 0) {
      return {
        title: 'Não foi possível acessar o servidor',
        description: 'A aplicação não conseguiu se comunicar com o servidor neste momento.',
        helper: 'Verifique sua conexão e tente atualizar a página para retomar o fluxo.',
        iconType: 'connection',
      };
    }

    if (code === 404) {
      return {
        title: 'Recurso não encontrado',
        description: 'O recurso acessado não existe ou foi removido.',
        helper: 'Verifique o endereço digitado ou volte para a página inicial.',
        iconType: 'not-found',
      };
    }

    if (code === 401) {
      return {
        title: 'Não autorizado',
        description: 'Sua sessão expirou ou não foi possível validar sua autenticação.',
        helper: 'Faça login novamente para continuar.',
        iconType: 'unauthorized',
      };
    }

    if (code === 403) {
      return {
        title: 'Acesso negado',
        description: 'Você não possui permissão para acessar este recurso.',
        helper: 'Verifique se está no perfil correto ou entre em contato com o suporte.',
        iconType: 'forbidden',
      };
    }

    if (code === 500) {
      return {
        title: 'Ocorreu um erro interno na aplicação',
        description: 'Encontramos um problema inesperado ao processar sua solicitação.',
        helper: 'Tente atualizar a página. Se o problema persistir, entre em contato com o suporte.',
        iconType: 'server',
      };
    }

    return {
      title: 'Ocorreu um erro inesperado',
      description: 'Não foi possível concluir a operação solicitada.',
      helper: 'Atualize a página para tentar novamente. Se o erro continuar, informe o código ao suporte.',
      iconType: 'generic',
    };
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.errorCode = params.get('code') ?? '500';
        this.cdr.markForCheck();
      });

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.returnUrl = this.criticalErrorNavigationService.resolveFallbackUrl(params.get('returnUrl'));
        this.backendMessage = params.get('msg');
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reloadPreviousPage(): void {
    this.isReloading = true;
    this.cdr.markForCheck();
    window.location.assign(this.returnUrl || '/');
  }
}
